<template>
  <v-container v-scroll="onScroll" class="px-3 px-sm-6 mt-2" fluid>
    <v-row class="mb-12">
      <v-col cols="12">
        <v-row class="align-end" no-gutters>
          <v-col cols="12" sm="8" lg="10">
            <h1 class="text-h4 font-weight-bold">Limited Edition Prints</h1>
            <h2 class="text-subtitle-1 mb-0">
              Buy prints with a certificate of authenticity granted by their
              creators
            </h2>
          </v-col>
          <v-col class="text-right" cols="12" sm="4" lg="2">
            <Filters
              :categories="categories"
              :on-apply="onApply"
              inactive-button-title="Refine by"
              from="prints"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <div>
          <v-row>
            <v-col
              v-for="(photo, i) in photos"
              :key="i"
              cols="12"
              md="6"
              lg="4"
            >
              <v-card outlined>
                <v-img
                  :src="$asa.getImageUrl(photo, 'thumb')"
                  :alt="photo.title"
                  :class="$vuetify.theme.dark ? 'dark' : ''"
                  class="thumb-preview pointer"
                  width="100%"
                  contain
                  min-height="400px"
                  max-height="400px"
                  @click="openViewPrintDialog(photo)"
                >
                  <template #placeholder>
                    <v-row
                      class="fill-height ma-0"
                      align="center"
                      justify="center"
                    >
                      <v-progress-circular
                        indeterminate
                        color="tertiary"
                      ></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </v-card>
            </v-col>
          </v-row>
          <div>
            <div v-intersect="onIntersect" />
            <div v-if="loading" class="text-center">
              <v-icon class="fa-spin my-3"> fal fa-spinner </v-icon>
            </div>
            <div
              v-if="!loading && photos.length === 0 && mounted"
              class="text-center"
            >
              <v-alert outlined color="primary">
                <v-icon class="mr-3">far fa-sad-cry</v-icon>No results for this
                search
              </v-alert>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    <ViewPrintDialog />
  </v-container>
</template>

<script>
import goTo from 'vuetify/lib/services/goto'
import { mapState } from 'vuex'
import Filters from '@/components/galleries/Filters.vue'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    Filters,
    ViewPrintDialog: () => import('@/components/prints/ViewPrintDialog.vue')
  },
  middleware: ['header'],
  data() {
    return {
      loading: false,
      offsetTop: 0,
      mounted: false
    }
  },
  async fetch({ store, $asa }) {
    try {
      await store.dispatch('account/getCategories')
    } catch (error) {
      $asa.appInsights('Pages: shop/prints/index.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Order Prints | Limited Edition',
      description: 'Stunning Images. Certificate of Authenticity. Best Prices. International Free Shipping.'
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      photos: state => state.photos.printsList,
      isLastPage: state => state.photos.printsIsLastPage,
      page: state => state.photos.printsPage,
      categories: state => state.account.categories,
      selectedCategoryText: state => state.photos.selectedCategoryText
    })
  },
  async mounted() {
    this.mounted = true
    if (this.$route.query.id) {
      const photoId = Number(this.$route.query.id)
      try {
        const photo = await this.$axios.$get(`/photos/${photoId}`)
        this.openViewPrintDialog(photo)
      } catch (error) {
        this.$asa.appInsights('Pages: shop/prints/index.vue - method: mounted', error)
      }
    }
    if (this.$route.query.cid && this.$route.query.category) {
      EventBus.$emit('set-category', {
        value: this.$route.query.cid,
        text: this.$route.query.category
      })
      return
    }
    if (
      this.photos.length === 0 ||
      this.$route.query.back !== 'yes' ||
      (this.$route.query.category &&
        this.$route.query.category !==
          this.$store.state.photos.selectedCategoryText)
    ) {
      try {
        this.loading = true
        await this.$store.dispatch('photos/resetPrintsGallery')
        const data = {
          page: this.page,
          back: !!this.$route.query.back
        }
        await this.$store.dispatch('photos/getLicensed', data)
      } catch (error) {
        this.$asa.appInsights('Pages: shop/prints/index.vue - method: mounted', error)
      } finally {
        this.loading = false
      }
    }
    if (
      this.$route.query.back === 'yes' &&
      this.$route.query.st &&
      this.photos.length > 0
    ) {
      goTo(Number(this.$route.query.st))
    }
  },
  methods: {
    onIntersect(entries) {
      if (
        entries[0].intersectionRatio > 0 &&
        !this.isLastPage &&
        this.photos.length > 0 &&
        !this.loading
      ) {
        this.loadMore()
      }
    },
    async loadMore() {
      this.loading = true
      try {
        await this.$store.dispatch('photos/getLicensed', {
          page: this.page + 1
        })
      } catch (error) {
        this.$asa.appInsights('Pages: shop/prints/index.vue - method: loadMore', error)
      } finally {
        this.loading = false
      }
    },
    openViewPrintDialog(photo) {
      this.$store.dispatch('prints/openDialog', photo)
    },
    getQueries() {
      const q = this.$route.query
      let query = ''
      query += `&st=${this.offsetTop}`
      if (q.category) query += `&category=${q.category}`
      return query
    },
    onScroll() {
      setTimeout(() => {
        const top = document.getElementById('app').getBoundingClientRect().top * -1
        this.offsetTop = top
      })
    },
    async onApply(filters) {
      await this.$store.dispatch('photos/resetPrintsGallery')
      await this.$store.dispatch('photos/setFilters', filters)
      const data = {
        page: this.page
      }
      try {
        this.loading = true
        await this.$store.dispatch('photos/getLicensed', data)
      } catch (error) {
        this.$asa.appInsights('Pages: shop/prints/index.vue - method: onApply', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.thumb-preview {
  background-color: #212121;
  border: 2px solid rgba(0, 0, 0, 0.54);
}
.thumb-preview.dark {
  background-color: #000;
}
</style>
